import React, { useMemo, useState } from 'react'
import { Options, Splide, SplideSlide } from '@splidejs/react-splide'
import { Grid } from '@splidejs/splide-extension-grid'
import '@splidejs/react-splide/css'
import {
    PaginationData,
    PaginationItem,
    Splide as SplideType
} from '@splidejs/splide/dist/types'
import { CardProduct } from 'components/card/CardProduct'
import { ProductCategoryFilter } from './ProductCategoryFilter'
import { SelectedPackagesTray } from './SelectedPackagesTray'
import { SelectedPacket, ProductWithAddOns } from './enrollFormTypes'
import { constants } from 'helpers'
import './enrollForm.scss'
import { useAppSelector, useScreenSize } from 'hooks'
import { Spinner } from 'components/spinner/Spinner'
import { useTranslation } from 'react-i18next'

type ProductSelectionProps = {
    onProductSelected: (product: ProductWithAddOns) => void
    selectedPackets?: SelectedPacket[]
    productListWithAddOns: ProductWithAddOns[]
    onClickHandler?: VoidFunction
    btnText?: string
}

const normalizeCategory = (category?: string) =>
    (category || '').trim().toLowerCase()

export const ProductSelection = ({
    selectedPackets = [],
    onProductSelected,
    productListWithAddOns,
    onClickHandler,
    btnText
}: ProductSelectionProps) => {
    const { t } = useTranslation()
    const screenSize = useScreenSize()
    const [selectedCategory, setSelectedCategory] = useState<string | null>(
        null
    )

    const { loading } = useAppSelector((state) => state.enrollment)

    const isMobile = screenSize.screenWidth < constants.MOBILE_SIZE
    const splideConfig: Options = {
        perMove: 1,
        perPage: 1,
        pagination: true,
        paginationDirection: 'ltr',
        autoHeight: true,
        autoWidth: false,
        flickPower: 10,
        width: screenSize.screenWidth * 0.9,
        arrows: false,
        grid: {
            rows: 3,
            cols: 2,
            gap: { row: '5%' }
        }
    }
    if (!isMobile) {
        splideConfig.width = undefined
        splideConfig.autoWidth = true
    }

    const onPaginationMounted = (
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        splide: SplideType,
        data: PaginationData,
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        item: PaginationItem
    ) => {
        data.list.classList.add('product-selection-custom-pagination')
    }

    const catalogProducts = useMemo(
        () =>
            productListWithAddOns.filter((product) => {
                const allDependsOnHaveLength =
                    product.product_prices?.every(
                        (productPrice) => productPrice.depends_on.length > 0
                    ) ?? false
                return !(allDependsOnHaveLength && product.nbSelectedUnits < 1)
            }),
        [productListWithAddOns]
    )

    const categories = useMemo(() => {
        const seen = new Set<string>()
        const ordered: string[] = []

        catalogProducts.forEach((product) => {
            if (product.isAddOn || !product.category) {
                return
            }
            const normalized = normalizeCategory(product.category)
            if (!normalized || seen.has(normalized)) {
                return
            }
            seen.add(normalized)
            ordered.push(product.category)
        })

        return ordered
    }, [catalogProducts])

    const filteredProducts = useMemo(() => {
        if (!selectedCategory) {
            return catalogProducts
        }
        const selected = normalizeCategory(selectedCategory)
        return catalogProducts.filter(
            (product) => normalizeCategory(product.category) === selected
        )
    }, [catalogProducts, selectedCategory])

    const splideKey = useMemo(
        () =>
            `${selectedCategory || 'all'}|${filteredProducts
                .map((p) => `${p.sf_id}:${p.nbSelectedUnits}`)
                .join('|')}`,
        [filteredProducts, selectedCategory]
    )

    const hasSelection = selectedPackets.length > 0

    return (
        <>
            {loading ? (
                <Spinner size="90" pageSpinner={true} fullscreen={true} />
            ) : (
                <div
                    className={`enrollment-button--packages${
                        hasSelection
                            ? ' enrollment-button--packages-with-tray'
                            : ''
                    }`}
                >
                    <ProductCategoryFilter
                        categories={categories}
                        selectedCategory={selectedCategory}
                        onSelectCategory={setSelectedCategory}
                    />
                    {filteredProducts.length > 0 && (
                        <Splide
                            key={splideKey}
                            onPaginationMounted={onPaginationMounted}
                            extensions={{ Grid }}
                            options={splideConfig}
                        >
                            {filteredProducts.map((product) => (
                                <React.Fragment key={product.sf_id}>
                                    <SplideSlide key={product.sf_id}>
                                        <CardProduct
                                            product={product}
                                            onClick={() => {
                                                onProductSelected(product)
                                            }}
                                            isAddOn={product.isAddOn}
                                        />
                                    </SplideSlide>
                                </React.Fragment>
                            ))}
                        </Splide>
                    )}
                    {hasSelection && (
                        <SelectedPackagesTray
                            selectedPackets={selectedPackets}
                            onContinue={() => onClickHandler?.()}
                            continueLabel={btnText || t('pay')}
                        />
                    )}
                </div>
            )}
        </>
    )
}
